import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { useTaxEnabled, billingAddressFor, BillingZipField } from '@/components/BillingZip';

import { stripePromise } from '@/lib/stripe';

/**
 * Inner form component — must be inside <Elements> provider.
 */
function CheckoutForm({ amount, taxAmount = 0, processingFee = 0, chargedAmount, description, transactionId, paymentIntentId, onSuccess, onError, onCancel, theme = 'light' }) {
  const payTotal = chargedAmount ?? amount;
  const dark = theme === 'dark';
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [pendingActivation, setPendingActivation] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setErrorMsg('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      // Required by Stripe at confirmation, even though card payments don't redirect.
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: 'if_required', // no redirect for card payments
    });

    if (error) {
      setErrorMsg(error.message || 'Payment failed');
      setProcessing(false);
      onError?.(error.message);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Externally created intents (tips, donations) have no platform transaction to settle
      if (!transactionId) {
        setSucceeded(true);
        onSuccess?.(paymentIntent);
        return;
      }
      // Tell backend to process the purchase
      const confirm = () => {
        const token = localStorage.getItem('token');
        return axios.post(
          `${API}/stripe/confirm-payment`,
          { transaction_id: transactionId, payment_intent_id: paymentIntentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      };
      try {
        await confirm();
        setSucceeded(true);
        onSuccess?.();
      } catch (err) {
        console.error('Confirm error, retrying once:', err);
        try {
          await new Promise(r => setTimeout(r, 2500));
          await confirm();
          setSucceeded(true);
          onSuccess?.();
        } catch (retryErr) {
          console.error('Confirm failed twice:', retryErr);
          // The card was charged, so never show a failure here. The
          // payment_intent.succeeded webhook grants the purchase as a backstop.
          setSucceeded(true);
          setPendingActivation(true);
          onSuccess?.();
        }
      }
    } else {
      setErrorMsg('Payment was not completed. Please try again.');
      setProcessing(false);
    }
  };

  if (succeeded) {
    return (
      <div className="text-center py-6" data-testid="checkout-success">
        <CheckCircle className={`w-14 h-14 mx-auto mb-3 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`} />
        <h3 className={`text-xl font-bold mb-1 ${dark ? 'text-emerald-300' : 'text-emerald-700'}`}>Payment Successful!</h3>
        <p className={`text-sm ${dark ? 'text-gray-400' : 'text-[#7a6a58]'}`}>
          {pendingActivation
            ? 'Your payment went through. Access is being applied — refresh in a moment if it has not appeared.'
            : 'Your purchase has been processed.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="stripe-checkout-form">
      <div className={`mb-4 p-4 rounded-xl border ${dark ? 'bg-white/5 border-white/10' : 'bg-white border-[#e4dbcd]'}`}>
        <PaymentElement
          options={{
            layout: 'tabs',
            defaultValues: { billingDetails: {} },
          }}
        />
      </div>

      {errorMsg && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="checkout-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {errorMsg}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-base font-semibold"
        data-testid="pay-now-btn"
      >
        {processing ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
        ) : (
          <><Lock className="w-4 h-4 mr-2" /> Pay ${payTotal?.toFixed(2)}</>
        )}
      </Button>

      <div className={`flex items-center justify-center gap-1.5 mt-3 text-xs ${dark ? 'text-gray-500' : 'text-[#9a8a78]'}`}>
        <Lock className="w-3 h-3" />
        <span>Card info goes directly to Stripe — never stored on our servers</span>
      </div>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className={`w-full mt-3 text-xs underline ${dark ? 'text-gray-400' : 'text-[#7a6a58]'}`}
          data-testid="checkout-cancel-btn"
        >
          Cancel
        </button>
      )}
    </form>
  );
}


/**
 * StripeInAppCheckout — self-contained component that creates a PaymentIntent,
 * renders Stripe Elements, and processes the purchase upon success.
 *
 * Props:
 *   itemType    — 'tier_upgrade' | 'workshop' | 'live_stream' | 'membership'
 *   itemId      — workshop/stream id (optional)
 *   tier        — for tier upgrades
 *   paymentOption — 'yearly', 'monthly_6', etc.
 *   isAddon     — boolean for drip removal
 *   onSuccess   — callback after payment succeeds
 *   onError     — callback on error
 *   onFreeUpgrade — callback when upgrade is free (no payment needed)
 */
export default function StripeInAppCheckout({
  itemType,
  itemId,
  tier,
  paymentOption,
  purchaseType,
  isAddon = false,
  clientSecret: externalClientSecret,
  amount: externalAmount,
  netAmount: externalNetAmount,
  processingFee: externalProcessingFee = 0,
  label,
  onSuccess,
  onError,
  onCancel,
  onFreeUpgrade,
  onPriced,
  theme = 'light',
}) {
  const [clientSecret, setClientSecret] = useState(externalClientSecret || null);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [amount, setAmount] = useState(externalNetAmount ?? externalAmount ?? 0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [processingFee, setProcessingFee] = useState(externalProcessingFee);
  const [chargedAmount, setChargedAmount] = useState(externalAmount ?? null);
  const [description, setDescription] = useState(label || '');
  const [loading, setLoading] = useState(!externalClientSecret);
  const [initError, setInitError] = useState('');
  const taxEnabled = useTaxEnabled();
  const [zip, setZip] = useState('');
  const [zipConfirmed, setZipConfirmed] = useState(false);
  // Promotions: coaching codes, workshop codes and livestream codes/sales. The
  // backend decides the discount; typing a code just re-prices the intent.
  const [codeInput, setCodeInput] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [discount, setDiscount] = useState(null);
  const supportsCodes = !isAddon
    && ['tier_upgrade', 'workshop', 'live_stream'].includes(itemType)
    // Coaching codes are for programs paid in full, so the box would only ever
    // refuse on a payment plan.
    && !(itemType === 'tier_upgrade' && paymentOption === 'plan');

  // Tips and donations create their own PaymentIntent server-side and pass the
  // secret straight in — there is nothing for this component to initialise.
  const externalMode = Boolean(externalClientSecret);

  useEffect(() => {
    let cancelled = false;
    if (externalMode) return;
    if (taxEnabled === null) return;              // still resolving — don't fire a doomed intent
    if (taxEnabled && !zipConfirmed) { setLoading(false); return; }

    async function init() {
      setLoading(true);
      setInitError('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/stripe/create-payment-intent`,
          {
            item_type: itemType,
            item_id: itemId,
            tier,
            payment_option: paymentOption,
            purchase_type: purchaseType,
            is_addon: isAddon,
            billing_address: billingAddressFor(zip),
            coupon_code: appliedCode || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (cancelled) return;

        // Handle free upgrade
        if (res.data.free_upgrade) {
          toast.success(res.data.message);
          onFreeUpgrade?.();
          return;
        }

        setClientSecret(res.data.client_secret);
        setTransactionId(res.data.transaction_id);
        setPaymentIntentId(res.data.payment_intent_id);
        setAmount(res.data.amount);
        setTaxAmount(res.data.tax_amount || 0);
        setProcessingFee(res.data.processing_fee || 0);
        setChargedAmount(res.data.amount_charged ?? res.data.amount);
        setDescription(res.data.description);
        setDiscount(res.data.discount || null);
        // Let a parent screen keep its own summary total honest once a code lands.
        onPriced?.({
          amount: res.data.amount,
          amountCharged: res.data.amount_charged ?? res.data.amount,
          discount: res.data.discount || null,
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err.response?.data?.detail || 'Failed to initialize checkout';
        // A code that cannot be used must never block the purchase — drop it,
        // say why, and re-price at the normal rate.
        if (appliedCode) {
          setCodeError(msg);
          setAppliedCode('');
          return;
        }
        setInitError(msg);
        onError?.(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => { cancelled = true; };
  }, [itemType, itemId, tier, paymentOption, purchaseType, isAddon, taxEnabled, zipConfirmed, externalMode, appliedCode]);

  if (taxEnabled === true && !zipConfirmed && !externalMode) {
    return (
      <div data-testid="checkout-billing-zip-step">
        <BillingZipField value={zip} onChange={setZip} theme={theme} />
        <Button
          onClick={() => setZipConfirmed(true)}
          disabled={zip.trim().length < 5}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5 text-base font-semibold"
          data-testid="checkout-zip-continue-btn"
        >
          Continue to payment
        </Button>
      </div>
    );
  }

  const dark = theme === 'dark';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3" data-testid="checkout-loading">
        <Loader2 className={`w-8 h-8 animate-spin ${dark ? 'text-teal-400' : 'text-teal-600'}`} />
        <p className={`text-sm ${dark ? 'text-gray-400' : 'text-[#7a6a58]'}`}>Preparing secure checkout...</p>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3 text-center" data-testid="checkout-init-error">
        <AlertCircle className={`w-8 h-8 ${dark ? 'text-red-400' : 'text-red-500'}`} />
        <p className={`text-sm ${dark ? 'text-red-300' : 'text-red-600'}`}>{initError}</p>
      </div>
    );
  }

  if (!clientSecret) return null;

  return (
    <div data-testid="stripe-in-app-checkout">
      {/* Amount banner */}
      <div
        className={`text-center mb-4 p-3 rounded-lg border ${
          dark ? 'bg-white/5 border-white/10' : 'bg-[#f8f6f2] border-[#e4dbcd]'
        }`}
      >
        <p className={`text-xs mb-0.5 ${dark ? 'text-gray-400' : 'text-[#7a6a58]'}`}>{description}</p>
        <p className={`text-2xl font-bold ${dark ? 'text-teal-300' : 'text-teal-700'}`}>
          ${(chargedAmount ?? amount).toFixed(2)}
        </p>
        {discount && (
          <p
            className={`text-xs mt-1 font-medium ${dark ? 'text-emerald-300' : 'text-emerald-700'}`}
            data-testid="checkout-discount-line"
          >
            {discount.label} — ${discount.amount_off.toFixed(2)} off
          </p>
        )}
        {(taxAmount > 0 || processingFee > 0) && (
          <p
            className={`text-xs mt-1 ${dark ? 'text-gray-400' : 'text-[#7a6a58]'}`}
            data-testid="checkout-tax-line"
          >
            ${amount.toFixed(2)}
            {taxAmount > 0 && ` + $${taxAmount.toFixed(2)} sales tax`}
            {processingFee > 0 && ` + $${processingFee.toFixed(2)} processing`}
          </p>
        )}
      </div>

      {supportsCodes && (
        <div className="mb-4" data-testid="checkout-promo-block">
          {appliedCode ? (
            <div
              className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm ${
                dark ? 'bg-emerald-500/10 border-emerald-400/30 text-emerald-200'
                     : 'bg-emerald-50 border-emerald-200 text-emerald-800'
              }`}
            >
              <span data-testid="checkout-applied-code">Code {appliedCode} applied</span>
              <button
                type="button"
                onClick={() => { setAppliedCode(''); setCodeInput(''); setCodeError(''); }}
                className="text-xs underline"
                data-testid="checkout-remove-code-btn"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeError(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && codeInput.trim()) {
                    e.preventDefault();
                    setAppliedCode(codeInput.trim());
                  }
                }}
                placeholder="Discount code"
                className={`flex-1 px-3 py-2 rounded-lg border text-sm tracking-wide ${
                  dark ? 'bg-white/5 border-white/15 text-white placeholder:text-gray-500'
                       : 'bg-white border-[#e4dbcd] text-[#3f352a] placeholder:text-[#a2957f]'
                }`}
                data-testid="checkout-promo-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={!codeInput.trim()}
                onClick={() => setAppliedCode(codeInput.trim())}
                className={dark ? 'border-white/20 text-gray-200 hover:bg-white/10' : ''}
                data-testid="checkout-apply-code-btn"
              >
                Apply
              </Button>
            </div>
          )}
          {codeError && (
            <p className="text-xs mt-1.5 text-red-500" data-testid="checkout-promo-error">
              {codeError}
            </p>
          )}
        </div>
      )}

      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: dark ? 'night' : 'stripe',
            variables: {
              colorPrimary: '#14b8a6',
              colorBackground: dark ? '#1e293b' : '#ffffff',
              colorText: dark ? '#e2e8f0' : '#1f2937',
              colorDanger: '#ef4444',
              borderRadius: '8px',
              fontFamily: 'system-ui, sans-serif',
            },
          },
        }}
      >
        <CheckoutForm
          amount={amount}
          taxAmount={taxAmount}
          processingFee={processingFee}
          chargedAmount={chargedAmount}
          description={description}
          transactionId={transactionId}
          paymentIntentId={paymentIntentId}
          onSuccess={onSuccess}
          onError={onError}
          onCancel={onCancel}
          theme={theme}
        />
      </Elements>
    </div>
  );
}
